import { MutableRefObject } from 'react';
import { APPLICATION_FLOW_STATUS } from 'typescript/types/api/capital_shop/application_flows';
import { PaymentType } from 'typescript/constants/enums/payment';
import { Card, Payments } from '@square/web-payments-sdk-types';
import { usePaymentContext } from 'typescript/context/payment_context';
import { useCheckoutFlow } from 'typescript/context/checkout_flow';
import { useCreateFinancingDisclosures } from 'typescript/hooks/use_financing_disclosures_client';
import { useSubmitOrder } from 'typescript/apps/checkout_v3/components/payment/use_submit_order';
import { useCompleteOrderContext } from 'typescript/context/complete_order_context';
import { CompleteOrderError } from 'typescript/constants/enums/checkout';
import { useCheckoutExperiments } from 'typescript/context/checkout_experiments';

const useHandleFinancingSubmission = () => {
  const { orderNumber } = useCheckoutFlow();

  const {
    submittingPayment,
    setSubmittingPayment,
    setShowInstallmentDecision,
    setPollingCapitalShop,
    setDisclosureDocumentToken,
    setApplicationFlowStatus,
    setShowDisclosureModal,
  } = usePaymentContext();
  const {
    setCompleteOrderError,
    setShowCompletingOrderDialog,
    setShowAlternateCompletingCopy,
  } = useCompleteOrderContext();

  const { submitOrder } = useSubmitOrder(orderNumber);

  const { createFinancingDisclosure } = useCreateFinancingDisclosures({
    orderNumber,
  });

  const { financingUiEnabled } = useCheckoutExperiments();

  const handleFinancingDisclosure = ({
    sqCard,
    sqPayments,
    billingAddress,
  }: {
    sqCard: Card;
    sqPayments: MutableRefObject<Payments>;
    billingAddress: Record<string, string | boolean>;
  }) => {
    createFinancingDisclosure(
      {},
      {
        onSuccess: (data) => {
          const documentToken = data.disclosure_document_token;
          if (documentToken || financingUiEnabled) {
            setDisclosureDocumentToken(documentToken);
            setShowDisclosureModal(true);
            setSubmittingPayment(false);
            setShowCompletingOrderDialog(false);
            setShowAlternateCompletingCopy(false);
          } else {
            submitOrder({
              paymentMethod: sqCard,
              paymentType: PaymentType.CARD,
              billingAddress,
              sqPayments: sqPayments.current,
            });
          }
        },
        onError: () => {
          setCompleteOrderError(CompleteOrderError.GENERIC);
          setSubmittingPayment(false);
          setShowCompletingOrderDialog(false);
        },
      },
    );
  };

  const handleDecisionStatusChange = ({
    decisionStatus,
    sqCard,
    sqPayments,
    billingAddress,
  }: {
    decisionStatus: APPLICATION_FLOW_STATUS;
    sqCard: Card;
    sqPayments: MutableRefObject<Payments>;
    billingAddress: Record<string, string | boolean>;
  }) => {
    if (
      !submittingPayment ||
      decisionStatus === undefined ||
      decisionStatus === APPLICATION_FLOW_STATUS.SUBMITTED
    ) {
      return;
    }

    setPollingCapitalShop(false);

    if (
      decisionStatus === APPLICATION_FLOW_STATUS.PENDING ||
      decisionStatus === APPLICATION_FLOW_STATUS.DECLINED
    ) {
      setSubmittingPayment(false);
      setShowCompletingOrderDialog(false);
      setShowInstallmentDecision(true);
      setApplicationFlowStatus(decisionStatus);
    } else if (decisionStatus === APPLICATION_FLOW_STATUS.REVIEWED) {
      handleFinancingDisclosure({ sqCard, sqPayments, billingAddress });
    }
  };

  return {
    handleDecisionStatusChange,
    handleFinancingDisclosure,
  };
};

export default useHandleFinancingSubmission;
